import { InfoCircleFilled } from "@ant-design/icons";
import { Button, Form, Modal } from "antd";
import { message } from "utils/ciq-message";
import { useForm } from "antd/lib/form/Form";
import DisplayNameListPopover from "components/bulk-edit-material/display-name-list";
import CIQDatePicker from "components/custom-date-picker";
import { getFeatureDetail } from "components/feature-entity-linking-page/utils";
import {
  ESubmittalStatus,
  EWorkflowStatusDataBlock,
  SidepanelStyles
} from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import { APPLY_WORKFLOW_MUTATION } from "entity-app/shared-components/entity-dateblock/queries/mutation";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { upperCase } from "lodash";
import dayjs from "utils/dayjs-config";
import { useContext, useEffect, useMemo, useState } from "react";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";

type FeatureInstanceDetail = {
  id: string;
  name: string;
  workflow_instance_id?: string;
};

function BulkEditDeadlineDateModal({
  onUpdateDone,
  selectedRows,
  showEdit,
  setShowEdit,
  columnHeaderMap,
  featureTypeId
}: {
  onUpdateDone: () => void;
  selectedRows: any[];
  showEdit: boolean;
  setShowEdit: (show: any) => void;
  columnHeaderMap: any;
  featureTypeId: FeatureTypes;
}) {
  const { gqlClientForProject } = useContext(ProjectContext);
  const labelDatelineDate =
    columnHeaderMap?.manual_entry_date || "Deadline Date";
  const deadLineTitle = columnHeaderMap?.final_deadline_date || "Deadline";

  const [form] = useForm();

  const [inProgress, setInProgress] = useState(false);
  const [selectedFeatureDetail, setSelectedFeatureDetail] = useState<{
    NotCompleted: Array<FeatureInstanceDetail>;
    Completed: Array<FeatureInstanceDetail>;
  }>({ NotCompleted: [], Completed: [] });

  const [isDiff, setIsDiff] = useState(false);
  const [inputData, setInputData] = useState<{
    manual_entry_date: dayjs.Dayjs | null;
  }>({ manual_entry_date: null });

  const featureDetail = useMemo(
    () => getFeatureDetail(featureTypeId),
    [featureTypeId]
  );

  const [applyWorkflowMutation] = useCIQMutation(APPLY_WORKFLOW_MUTATION, {
    client: gqlClientForProject
  });

  useEffect(() => {
    const NotCompleted: FeatureInstanceDetail[] = [];
    const Completed: FeatureInstanceDetail[] = [];
    let manualEntryDate: string | null =
      selectedRows.length > 0 ? selectedRows[0].manual_entry_date : null;

    selectedRows.forEach((row: any) => {
      const sequenceId = row?.sequence_id || row?.auto_inc_value || "";
      const name = row?.title || "";

      const item: FeatureInstanceDetail = {
        id: row.id,
        name: `${sequenceId} - ${name}`,
        workflow_instance_id:
          row.date_block?.id || row.feature_workflow_instance_id
      };

      const submittalWFStatus = upperCase(row?.workflow_status?.code || "");
      if (
        row?.workflow_status === EWorkflowStatusDataBlock.WF_COMPLETED ||
        submittalWFStatus === ESubmittalStatus[ESubmittalStatus.DONE]
      ) {
        Completed.push(item);
      } else {
        NotCompleted.push(item);

        // Unique manual_entry_date
        if (manualEntryDate !== row.manual_entry_date) {
          manualEntryDate = null;
        }
      }
    });
    setSelectedFeatureDetail({ NotCompleted, Completed });
    setInputData({
      manual_entry_date: manualEntryDate
        ? DateUtils.dateTimeObj(manualEntryDate)
        : null
    });
  }, [form, selectedRows]);

  const onCancelClick = () => {
    setShowEdit(false);
    onUpdateDone();
  };
  const onFinish = async () => {
    setInProgress(true);
    try {
      const manualEntryDateStr = inputData.manual_entry_date
        ? DateUtils.formatDateWithLunchTime(inputData.manual_entry_date)
        : null;

      const objects = selectedFeatureDetail.NotCompleted.map((item) => ({
        feature_instance_id: item.id,
        workflow_instance_id: item.workflow_instance_id,
        manual_entry_date: manualEntryDateStr
      }));

      const updateResponse = await applyWorkflowMutation({
        variables: {
          input: objects,
          metadata: {
            request_source: "INLINE_EDIT_LOG_PAGE"
          }
        }
      });
      if (updateResponse.success) {
        message.success("Updated successfully.");
        setShowEdit(false);
        onUpdateDone();
      } else {
        message.error("Failed to update.");
      }
    } catch (errorInfo) {
      console.error("Failed:", errorInfo);
    } finally {
      setInProgress(false);
    }
  };

  return (
    <Modal
      title={[
        <div key="title" data-testid="bulkfeatureeditdatelinedate-modal">
          <span>Bulk Edit {labelDatelineDate}</span>
          <span className="text-xs font-medium ml-2 mr-2 text-black text-opacity-60">
            |
          </span>
          <span className="text-xs font-medium text-black text-opacity-60">
            {selectedRows.length} Selected {featureDetail.titlePlural}
          </span>
        </div>
      ]}
      className="custom-drawer"
      style={SidepanelStyles.style}
      width={420}
      open={showEdit}
      onCancel={onCancelClick}
      destroyOnHidden
      data-testid={`bulkfeatureedit${featureDetail.feature}-modal-root`}
      footer={[
        <div className="flex w-full space-x-3 justify-end" key={100}>
          <Button
            onClick={onCancelClick}
            key={101}
            disabled={inProgress}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            disabled={!isDiff || inProgress}
            loading={inProgress}
            onClick={onFinish}
            key={102}
            data-testid="apply-button"
          >
            Apply
          </Button>
        </div>
      ]}
    >
      {selectedFeatureDetail.Completed.length > 0 && (
        <div className="pb-3">
          <div className="flex items-center space-x-2 text-xs px-3 py-2 border border-solid border-ciq-blue-300 bg-ciq-blue-100 rounded">
            <InfoCircleFilled className="text-ciq-blue-700" />
            <div className="text-black text-opacity-80">
              <DisplayNameListPopover
                list={selectedFeatureDetail.Completed}
                placement="left"
              >
                {selectedFeatureDetail.Completed.length}
              </DisplayNameListPopover>{" "}
              {selectedFeatureDetail.Completed.length === 1
                ? `${featureDetail.title} is`
                : `${featureDetail.titlePlural} are`}{" "}
              completed and will not be updated.
            </div>
          </div>
        </div>
      )}

      <div className="text-black text-opacity-90 font-semibold">
        Set a {labelDatelineDate} for the {featureDetail.title}
      </div>
      <div className="text-black text-opacity-50 text-xs">
        The earliest of the {labelDatelineDate} and Start/End Date of Linked
        Entities will be considered as the {deadLineTitle}.
      </div>
      <Form
        layout="vertical"
        preserve
        form={form}
        className="space-y-3 min-h-full pt-3"
        disabled={inProgress}
        data-testid="bulk-dateline-date-edit-form"
      >
        <div className="flex-col overflow-y overflow-x-hidden h-full px-1">
          <Form.Item
            label={labelDatelineDate}
            data-testid="dateline-date-form-item"
          >
            <CIQDatePicker
              className="w-full "
              format={DATE_FORMAT_MMDDYYYY}
              placeholder={`Select ${labelDatelineDate}`}
              placement="topLeft"
              value={inputData.manual_entry_date}
              onChange={(date: any) => {
                setIsDiff(true);
                setInputData({ manual_entry_date: date });
              }}
              renderExtraFooter={() =>
                inputData.manual_entry_date ? (
                  <div className="flex justify-center py-2 w-full">
                    <Button
                      onClick={() => {
                        setIsDiff(true);
                        setInputData({ manual_entry_date: null });
                      }}
                      data-testid="index-button-clear-date"
                    >
                      Clear Date
                    </Button>
                  </div>
                ) : null
              }
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}
export default BulkEditDeadlineDateModal;
